import { z } from 'zod';
import { ProviderMessageSchema, ProviderStatusSchema, ProviderTypeSchema } from './provider.js';
/**
 * Events normalisés émis par les workers bridges et propagés via Redis
 * pub/sub vers le backend HTTP (qui les relaie ensuite sur le WebSocket
 * vers les clients).
 *
 * Cf. ADR-009. Le module `@nexus/backend/integrations/core/event-bus.ts`
 * publie sur `bridge:event:<providerType>` et le backend HTTP est abonné.
 */
// ----- Common envelope -------------------------------------------------------
const BaseBridgeEventSchema = z.object({
    /** Identifie la session messagerie source (UUID Nexus, pas l'externalId). */
    sessionId: z.string().uuid(),
    /** Type de provider — utile pour debug et routing. */
    providerType: ProviderTypeSchema,
    /** Timestamp de l'event (ms epoch, fourni par le worker). */
    timestamp: z.number().int().nonnegative(),
});
// ----- Message events --------------------------------------------------------
export const BridgeMessageNewEventSchema = BaseBridgeEventSchema.extend({
    kind: z.literal('message:new'),
    message: ProviderMessageSchema,
});
export const BridgeMessageEditEventSchema = BaseBridgeEventSchema.extend({
    kind: z.literal('message:edit'),
    message: ProviderMessageSchema,
});
export const BridgeMessageDeleteEventSchema = BaseBridgeEventSchema.extend({
    kind: z.literal('message:delete'),
    channelExternalId: z.string(),
    externalMessageId: z.string(),
});
export const BridgeReactionEventSchema = BaseBridgeEventSchema.extend({
    kind: z.literal('message:reaction'),
    channelExternalId: z.string(),
    externalMessageId: z.string(),
    emoji: z.string(),
    byExternalUserId: z.string(),
    added: z.boolean(), // true = ajouté, false = retiré
});
// ----- Channel / lifecycle events --------------------------------------------
export const BridgeChannelUpsertEventSchema = BaseBridgeEventSchema.extend({
    kind: z.literal('channel:upsert'),
    externalId: z.string(),
    name: z.string(),
    channelType: z.enum(['text', 'dm', 'group_dm']),
});
export const BridgeStatusEventSchema = BaseBridgeEventSchema.extend({
    kind: z.literal('status:update'),
    status: ProviderStatusSchema,
});
export const BridgeHistorySyncedEventSchema = BaseBridgeEventSchema.extend({
    kind: z.literal('history:synced'),
    channelExternalId: z.string(),
    count: z.number().int().nonnegative(),
});
// ----- Discriminated union ---------------------------------------------------
export const BridgeEventSchema = z.discriminatedUnion('kind', [
    BridgeMessageNewEventSchema,
    BridgeMessageEditEventSchema,
    BridgeMessageDeleteEventSchema,
    BridgeReactionEventSchema,
    BridgeChannelUpsertEventSchema,
    BridgeStatusEventSchema,
    BridgeHistorySyncedEventSchema,
]);
// ----- Control commands (API HTTP → worker) ----------------------------------
export const BridgeControlSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('session:added'),
        sessionId: z.string().uuid(),
    }),
    z.object({
        kind: z.literal('session:removed'),
        sessionId: z.string().uuid(),
    }),
    z.object({
        kind: z.literal('session:reconnect'),
        sessionId: z.string().uuid(),
    }),
]);
/**
 * Helpers de nommage des topics Redis pub/sub. Les workers s'abonnent à
 * leur propre topic de contrôle, le backend HTTP s'abonne à tous les
 * topics d'events.
 */
export function eventTopic(providerType) {
    return `bridge:event:${providerType}`;
}
export function controlTopic(providerType) {
    return `bridge:control:${providerType}`;
}
//# sourceMappingURL=events.js.map